import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageCircle, Sparkles } from "lucide-react";
import { Toaster, toast } from "sonner";
import Navbar from "@/components/landing/Navbar";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import Footer from "@/components/landing/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SERVICES = [
  "Digital Marketing & Lead Gen",
  "Branding & Corporate Creative",
  "High-End Print & Media Systems",
  "Web Engineering & Architecture",
  "AI & Performance Automation",
  "Full 360° Agency Ecosystem",
  "Other",
];

const FIELDS = {
  name: "",
  company: "",
  website: "",
  mobile: "",
  email: "",
  service: "",
  other: "",
};

export default function CustomiseScope() {
  const [form, setForm] = useState(FIELDS);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const isOther = form.service === "Other";

  const isValid = useMemo(() => {
    if (!form.name.trim() || !form.company.trim() || !form.mobile.trim() || !form.email.trim() || !form.service) {
      return false;
    }
    if (isOther && !form.other.trim()) return false;
    // basic email check
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  }, [form, isOther]);

  const buildWhatsAppURL = () => {
    const encode = (v) => encodeURIComponent(v.trim() || "—");
    const url =
      "https://wa.me/918602265191?text=" +
      "Hey%20Dynamic%20X%20Media%2C%20I%20have%20customised%20my%20service%20scope.%20Here%20are%20my%20project%20details%3A%0A%0A" +
      `-%20Name%3A%20${encode(form.name)}%0A` +
      `-%20Company%3A%20${encode(form.company)}%0A` +
      `-%20Website%3A%20${encode(form.website)}%0A` +
      `-%20Service%20Selected%3A%20${encode(form.service)}%0A` +
      `-%20Other%20Details%3A%20${encode(form.other)}%0A` +
      `-%20Mobile%3A%20${encode(form.mobile)}%0A` +
      `-%20Email%3A%20${encode(form.email)}`;
    return url;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) {
      toast.error("Please fill in all required fields with a valid email.");
      return;
    }
    const url = buildWhatsAppURL();
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp with your custom scope…");
  };

  return (
    <div data-testid="customise-scope-page" className="relative bg-white">
      <AnnouncementBar />
      <Navbar />

      <main className="pt-40 pb-24 sm:pt-44">
        <section className="relative max-w-3xl mx-auto px-6 lg:px-8">
          {/* Back link */}
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.3em] font-semibold text-[#2770FD] hover:text-black transition-colors mb-6"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </Link>

          {/* Header */}
          <span className="inline-block text-xs uppercase tracking-[0.3em] font-semibold text-[#2770FD] mb-5">
            Custom Project Blueprint
          </span>
          <h1
            data-testid="cs-heading"
            className="font-display text-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            Customise Your <span className="text-accent-blue">Scope.</span>
          </h1>
          <p
            data-testid="cs-subtitle"
            className="mt-5 text-black/70 text-base sm:text-lg font-light leading-relaxed max-w-2xl"
          >
            Select your pillars, input your project details, and our automation will
            immediately compile a custom performance blueprint.
          </p>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            data-testid="cs-form"
            className="mt-12 space-y-5 rounded-2xl border border-black/10 bg-white p-6 sm:p-8 soft-blue-glow"
          >
            <div className="space-y-1.5">
              <Label htmlFor="cs-name" className="text-black font-semibold">
                Full Name <span className="text-[#2770FD]">*</span>
              </Label>
              <Input
                id="cs-name"
                data-testid="cs-input-name"
                placeholder="Aanya Kapoor"
                value={form.name}
                onChange={set("name")}
                required
                className="h-12 border-black/15 text-black placeholder:text-black/40 focus-visible:ring-[#2770FD]"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cs-company" className="text-black font-semibold">
                Company Name <span className="text-[#2770FD]">*</span>
              </Label>
              <Input
                id="cs-company"
                data-testid="cs-input-company"
                placeholder="DynamicXmedia"
                value={form.company}
                onChange={set("company")}
                required
                className="h-12 border-black/15 text-black placeholder:text-black/40 focus-visible:ring-[#2770FD]"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cs-website" className="text-black font-semibold">
                Website URL{" "}
                <span className="text-xs font-normal text-black/55">(If any)</span>
              </Label>
              <Input
                id="cs-website"
                type="text"
                data-testid="cs-input-website"
                placeholder="dynamicxmedia.com"
                value={form.website}
                onChange={set("website")}
                className="h-12 border-black/15 text-black placeholder:text-black/40 focus-visible:ring-[#2770FD]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label htmlFor="cs-mobile" className="text-black font-semibold">
                  Mobile Number <span className="text-[#2770FD]">*</span>
                </Label>
                <Input
                  id="cs-mobile"
                  type="tel"
                  data-testid="cs-input-mobile"
                  placeholder="+91 98765 43210"
                  value={form.mobile}
                  onChange={set("mobile")}
                  required
                  className="h-12 border-black/15 text-black placeholder:text-black/40 focus-visible:ring-[#2770FD]"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cs-email" className="text-black font-semibold">
                  Corporate Email <span className="text-[#2770FD]">*</span>
                </Label>
                <Input
                  id="cs-email"
                  type="email"
                  data-testid="cs-input-email"
                  placeholder="you@brand.com"
                  value={form.email}
                  onChange={set("email")}
                  required
                  className="h-12 border-black/15 text-black placeholder:text-black/40 focus-visible:ring-[#2770FD]"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cs-service" className="text-black font-semibold">
                Service Needed <span className="text-[#2770FD]">*</span>
              </Label>
              <Select
                value={form.service}
                onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
              >
                <SelectTrigger
                  id="cs-service"
                  data-testid="cs-select-service"
                  className="h-12 border-black/15 text-black focus:ring-[#2770FD]"
                >
                  <SelectValue placeholder="Choose a service pillar…" />
                </SelectTrigger>
                <SelectContent className="bg-white border-black/15">
                  {SERVICES.map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      data-testid={`cs-service-option-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      className="text-black focus:bg-[#2770FD]/10 focus:text-[#2770FD]"
                    >
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Conditional Other line — always active so users can type freely */}
            <div className="space-y-1.5">
              <Label htmlFor="cs-other" className="text-black font-semibold">
                Other / Custom Details
                {isOther && <span className="text-[#2770FD] ml-1">*</span>}
              </Label>
              <Input
                id="cs-other"
                data-testid="cs-input-other"
                placeholder="If 'Other' selected, please describe the service you need here…"
                value={form.other}
                onChange={set("other")}
                className="h-12 border-black/15 text-black placeholder:text-black/40 focus-visible:ring-[#2770FD]"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              data-testid="cs-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2770FD] hover:bg-[#1f5fe0] disabled:opacity-60 disabled:cursor-not-allowed text-white text-base font-semibold px-6 py-4 shadow-[0_12px_30px_-8px_rgba(39,112,253,0.55),0_0_0_1px_rgba(39,112,253,0.25)] transition-all duration-300 hover:-translate-y-0.5"
            >
              <MessageCircle className="h-4 w-4" fill="white" />
              Send Custom Scope via WhatsApp
              <ArrowRight className="h-5 w-5" />
            </button>

            <p className="text-[11px] text-black/55 text-center pt-1">
              By submitting, your details open in WhatsApp pre-filled for our team.
              We never share your data.
            </p>
          </form>

          {/* Trust line */}
          <div className="mt-10 text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-black/55">
              <Sparkles className="inline h-3.5 w-3.5 text-[#2770FD] mr-1.5 -mt-0.5" />
              Custom blueprint compiled · responses within 24 hours
            </p>
          </div>
        </section>
      </main>

      <Footer />
      <Toaster position="bottom-center" richColors />
    </div>
  );
}
